import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { getOptionsEngines, createOptionsEngine, updateOptionsEngine, deleteOptionsEngine, OptionsEngine } from '../../services/optionsTraderService';
import { canAccessAutoTrader } from '../../lib/entitlements';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';

const SYMBOLS = [
  { value: 'ES', label: 'ES (E-mini S&P 500)', multiplier: '$50/pt' },
  { value: 'MES', label: 'MES (Micro E-mini S&P 500)', multiplier: '$5/pt' },
  { value: 'SPX', label: 'SPX (S&P 500 Index)', multiplier: '$100/pt' },
  { value: 'SPY', label: 'SPY (S&P 500 ETF)', multiplier: '$100/pt' },
  { value: 'GC', label: 'GC (Gold Futures)', multiplier: '$100/oz' },
  { value: 'CL', label: 'CL (Crude Oil Futures)', multiplier: '$1000/bbl' },
];

const ALL_HORIZONS = [0, 1, 7, 14, 21, 30, 45, 60];

const cardStyle: React.CSSProperties = { background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: '20px 24px', marginBottom: 12 };
const labelStyle: React.CSSProperties = { fontSize: 13, color: '#9ca3af', marginBottom: 4, display: 'block' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 6, background: '#111827', color: '#f9fafb', border: '1px solid #374151', fontSize: 14 };
const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { padding: '10px 24px', background: '#6d28d9', color: '#f9fafb', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 };
const btnSecondary: React.CSSProperties = { ...btnPrimary, background: '#374151' };
const btnDanger: React.CSSProperties = { ...btnPrimary, background: '#991b1b', fontSize: 12, padding: '6px 12px' };
const btnSmall: React.CSSProperties = { padding: '4px 10px', borderRadius: 4, background: '#374151', color: '#f9fafb', border: 'none', fontSize: 12, cursor: 'pointer' };

type HorizonStrategy = 'lowest_dte' | 'highest_score' | 'highest_credit' | 'best_theta';

type TradeType = 'spread' | 'naked' | 'auto' | 'bear_call_spread' | 'jade_lizard' | 'iron_condor';

type FillStrategy = 'walk' | 'patient';

type ProfitCloseMode = 'smart' | 'strict';
type LossCloseMode = 'auto' | 'alerts';

type SignalSource = 'v2' | 'notches';
type NotchesPreset = 'conservative' | 'balanced' | 'aggressive';

type FormState = {
  name: string; symbol: string; tradeType: TradeType; horizons: number[]; spreadWidth: number; quantity: number;
  profitTargetPct: number; stopLossMultiplier: number; hardStopMultiplier: number;
  enableHardStopUnderlying: boolean; hardStopUnderlyingBuffer: number;
  exitStrategy: 'baseline' | 'convert';
  maxOpenPositions: number;
  maxDailyTrades: number; maxBpPct: number; minSpreadCredit: string;
  fillStrategy: FillStrategy; maxOrderAgeHours: number;
  fillWalkSteps: number; fillWalkIntervalMs: number; fillAdverseTicks: number;
  horizonStrategy: HorizonStrategy;
  profitCloseMode: ProfitCloseMode; postProfitCooldownHours: number;
  lossCloseMode: LossCloseMode;
  stopLossMinDte: number;
  paperMode: boolean; schedule: string;
  // Signal source (Notches = native preset-driven; V2 = legacy DTE-bucket iteration)
  signalSource: SignalSource;
  preset: NotchesPreset;
  notchesMaxDte: number;
  notchesMinDte: number;
  // Long-call hedge (paper-tested 2026-06-09; see docs/wiki/analysis/2026-06-09-es-long-hedge-backtest.md)
  hedgeWithLongCall: boolean;
  hedgeCallTargetDelta: number;
  hedgeCallMinDTE: number;
  // Credit-degradation gate (2026-10-07, percent-based)
  creditGateEnabled: boolean;
  creditGateMaxPct: number;
  creditGateOnBreach: 'skip' | 'proceed';
};

const defaultForm: FormState = {
  name: '', symbol: 'ES', tradeType: 'spread', horizons: [7, 14, 30, 45], spreadWidth: 50, quantity: 1,
  profitTargetPct: 50, stopLossMultiplier: 2, hardStopMultiplier: 0,
  enableHardStopUnderlying: false, hardStopUnderlyingBuffer: 0.02,
  exitStrategy: 'baseline',
  maxOpenPositions: 3, maxDailyTrades: 2, maxBpPct: 40,
  minSpreadCredit: '',
  fillStrategy: 'patient', maxOrderAgeHours: 24,
  fillWalkSteps: 3, fillWalkIntervalMs: 20000, fillAdverseTicks: 2,
  horizonStrategy: 'lowest_dte',
  profitCloseMode: 'smart', postProfitCooldownHours: 0,
  lossCloseMode: 'auto',
  stopLossMinDte: 21,
  paperMode: true, schedule: '30min',
  signalSource: 'notches', preset: 'conservative', notchesMaxDte: 60, notchesMinDte: 0,
  hedgeWithLongCall: false, hedgeCallTargetDelta: 0.20, hedgeCallMinDTE: 7,
  creditGateEnabled: true, creditGateMaxPct: 1, creditGateOnBreach: 'skip',
};

const PRESET_INFO: Record<NotchesPreset, { label: string; short: string }> = {
  conservative: {
    label: 'Conservative',
    short: 'Far-OTM, longer-DTE picks. Highest probability of profit, smaller credit per trade. Lowest risk.',
  },
  balanced: {
    label: 'Balanced',
    short: 'Mid-OTM, medium-DTE picks. Standard mix of credit and safety.',
  },
  aggressive: {
    label: 'Aggressive',
    short: 'Closer-to-ATM, shorter-DTE picks. Larger credit per trade, higher gamma risk near expiry.',
  },
};

const MULTIPLIER: Record<string, number> = { SPX: 100, SPXW: 100, SPY: 100, ES: 50, MES: 5, GC: 100, CL: 1000 };
const DEFAULT_MIN_CREDIT_DOLLARS: Record<string, number> = { SPX: 500, SPY: 50, ES: 100, MES: 25, GC: 150, CL: 100 };

const STRATEGY_INFO: Record<HorizonStrategy, { label: string; short: string; example: string }> = {
  lowest_dte: {
    label: 'Fastest turnover — shortest DTE',
    short: 'Pick the nearest-expiry horizon that meets your min credit. Fastest theta decay, fastest closes.',
    example: 'If 7-DTE spread = $5 and 30-DTE spread = $8, pick 7-DTE. Closes in days, frees capital faster.',
  },
  highest_score: {
    label: 'Highest AI score',
    short: "Pick the horizon whose best candidate has the highest ranking score (credit + delta + IV + skew + buffer combined).",
    example: 'If 14-DTE scores 0.12 and 7-DTE scores 0.08, pick 14-DTE — the model says it\'s the better setup even if it\'s further out.',
  },
  highest_credit: {
    label: 'Biggest income per trade',
    short: 'Pick the horizon with the largest spread credit, regardless of DTE. Fewer trades, larger $ each.',
    example: 'If 7-DTE spread = $5 and 30-DTE spread = $9, pick 30-DTE. Holds longer but collects more.',
  },
  best_theta: {
    label: 'Best theta decay (credit per day)',
    short: 'Pick the horizon with the highest spread-credit-divided-by-DTE. Fastest daily income accrual.',
    example: '7-DTE at $5 = $0.71/day beats 30-DTE at $9 = $0.30/day. Optimizes compounding velocity.',
  },
};

const OptionsEngines: React.FC = () => {
  const { user, token } = useAuth();
  const [engines, setEngines] = useState<OptionsEngine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>({ ...defaultForm });
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('legacy');

  const enabled = canAccessAutoTrader(user, 'tastytrade');
  const ttConnected = !!(user as any)?.tastyTradeConnected;
  const ttAccount = (user as any)?.tastyTradeAccountNumber || '';

  // Build connections list from user object
  const ttConnections = (user as any)?.tastyTradeConnections || [];
  const connections = React.useMemo(() => {
    const conns: { connectionId: string; label: string; accountNumber: string }[] = [];
    if (ttConnections.length > 0) {
      // Multi-connection: use the connections array from getUser
      ttConnections.forEach((c: any) => {
        conns.push({ connectionId: c.connectionId, label: c.label || 'Account', accountNumber: c.accountNumber });
      });
    } else if (ttConnected && ttAccount) {
      // Legacy single connection
      conns.push({ connectionId: 'legacy', label: 'Main Account', accountNumber: '***' + ttAccount.slice(-4) });
    }
    return conns;
  }, [ttConnections, ttConnected, ttAccount]);

  const loadEngines = () => {
    if (!token || !enabled) return;
    setLoading(true);
    getOptionsEngines(token).then(setEngines).catch(e => setError(e.message)).finally(() => setLoading(false));
  };

  useEffect(loadEngines, [token, enabled]);

  const resetForm = () => {
    setForm({ ...defaultForm });
    setDisclaimerAccepted(false);
    setShowCreate(false);
    setEditingId(null);
  };

  // Map the form's Trade Type (which combines strategy-family and put-side
  // variant into one dropdown) into the two backend fields it drives:
  //   - optionsConfig.tradeType (spread|naked|auto) — put-side implementation
  //   - optionsConfig.notchesStrategy (naked_put|bear_call_spread) — strategy family
  // "Bear Call Spread" is a distinct strategy; the other 3 stay on naked_put.
  const encodeTradeType = (uiTradeType: TradeType): { tradeType: 'spread' | 'naked' | 'auto'; notchesStrategy: 'naked_put' | 'bear_call_spread' | 'jade_lizard' | 'iron_condor' } => {
    if (uiTradeType === 'bear_call_spread') return { tradeType: 'spread', notchesStrategy: 'bear_call_spread' };
    if (uiTradeType === 'jade_lizard') return { tradeType: 'spread', notchesStrategy: 'jade_lizard' };
    if (uiTradeType === 'iron_condor') return { tradeType: 'spread', notchesStrategy: 'iron_condor' };
    return { tradeType: uiTradeType, notchesStrategy: 'naked_put' };
  };

  const handleCreate = async () => {
    if (!token) return;
    setSaving(true);
    setError('');
    try {
      const conn = connections.find(c => c.connectionId === selectedConnectionId);
      const { tradeType: encodedTradeType, notchesStrategy } = encodeTradeType(form.tradeType);
      const payload = {
        ...form,
        tradeType: encodedTradeType,
        notchesStrategy,
        minSpreadCredit: form.minSpreadCredit === '' ? null : parseFloat(form.minSpreadCredit) / (MULTIPLIER[form.symbol] || 5),
        creditDegradationGate: {
          enabled: form.creditGateEnabled,
          maxPctBelowSignal: form.creditGateMaxPct,
          onBreach: form.creditGateOnBreach,
        },
        connectionId: selectedConnectionId,
        accountNumber: conn?.accountNumber,
        disclaimerAccepted: isLive ? disclaimerAccepted : undefined,
      };
      await createOptionsEngine(token, payload);
      resetForm();
      loadEngines();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const handleEdit = (engine: OptionsEngine) => {
    const cfg = engine.optionsConfig || {} as any;
    setForm({
      name: engine.name || '',
      symbol: cfg.symbol || 'ES',
      // Reverse-map the two backend fields (tradeType + notchesStrategy) back
      // into the single UI Trade Type dropdown. Any non-naked_put strategy
      // wins when set; otherwise fall back to the raw put-side tradeType.
      tradeType: (cfg.notchesStrategy === 'bear_call_spread' ? 'bear_call_spread'
        : cfg.notchesStrategy === 'jade_lizard' ? 'jade_lizard'
        : cfg.notchesStrategy === 'iron_condor' ? 'iron_condor'
        : ((cfg.tradeType as TradeType) || 'spread')),
      horizons: cfg.horizons || [7, 14, 30, 45],
      spreadWidth: cfg.spreadWidth || 50,
      quantity: cfg.quantity || 1,
      profitTargetPct: cfg.profitTargetPct || 50,
      stopLossMultiplier: cfg.stopLossMultiplier || 2,
      hardStopMultiplier: typeof cfg.hardStopMultiplier === 'number' ? cfg.hardStopMultiplier : 0,
      enableHardStopUnderlying: cfg.enableHardStopUnderlying === true,
      hardStopUnderlyingBuffer: typeof cfg.hardStopUnderlyingBuffer === 'number' ? cfg.hardStopUnderlyingBuffer : 0.02,
      exitStrategy: (cfg.exitStrategy === 'convert' ? 'convert' : 'baseline'),
      maxOpenPositions: cfg.maxOpenPositions || 3,
      maxDailyTrades: cfg.maxDailyTrades || 2,
      maxBpPct: cfg.maxBpPct || 40,
      minSpreadCredit: cfg.minSpreadCredit != null ? String(Math.round(cfg.minSpreadCredit * (MULTIPLIER[cfg.symbol] || 5))) : '',
      fillStrategy: (cfg.fillStrategy as FillStrategy) || (['ES', 'MES'].includes(cfg.symbol) ? 'patient' : 'walk'),
      maxOrderAgeHours: cfg.maxOrderAgeHours || 24,
      fillWalkSteps: cfg.fillWalkSteps ?? 3,
      fillWalkIntervalMs: cfg.fillWalkIntervalMs ?? 20000,
      fillAdverseTicks: cfg.fillAdverseTicks ?? 2,
      horizonStrategy: (cfg.horizonStrategy as HorizonStrategy) || 'lowest_dte',
      signalSource: (cfg.signalSource as SignalSource) || 'notches',
      preset: (cfg.preset as NotchesPreset) || 'conservative',
      notchesMaxDte: typeof cfg.notchesMaxDte === 'number' ? cfg.notchesMaxDte : 60,
      notchesMinDte: typeof cfg.notchesMinDte === 'number' ? cfg.notchesMinDte : 0,
      profitCloseMode: (cfg.profitCloseMode as ProfitCloseMode) || 'smart',
      postProfitCooldownHours: typeof cfg.postProfitCooldownHours === 'number' ? cfg.postProfitCooldownHours : 0,
      lossCloseMode: (cfg.lossCloseMode as LossCloseMode) || 'auto',
      stopLossMinDte: typeof cfg.stopLossMinDte === 'number' ? cfg.stopLossMinDte : 21,
      paperMode: engine.paperMode !== false,
      schedule: (engine as any).schedule || '30min',
      hedgeWithLongCall: !!cfg.hedgeWithLongCall,
      hedgeCallTargetDelta: typeof cfg.hedgeCallTargetDelta === 'number' ? cfg.hedgeCallTargetDelta : 0.20,
      hedgeCallMinDTE: typeof cfg.hedgeCallMinDTE === 'number' ? cfg.hedgeCallMinDTE : 7,
      creditGateEnabled: cfg.creditDegradationGate ? cfg.creditDegradationGate.enabled !== false : true,
      creditGateMaxPct: typeof cfg.creditDegradationGate?.maxPctBelowSignal === 'number' ? cfg.creditDegradationGate.maxPctBelowSignal : 1,
      creditGateOnBreach: cfg.creditDegradationGate?.onBreach === 'proceed' ? 'proceed' : 'skip',
    });
    setEditingId(engine.engineId);
    setDisclaimerAccepted(false);
    setShowCreate(false);
  };

  const handleSaveEdit = async () => {
    if (!token || !editingId) return;
    setSaving(true);
    setError('');
    try {
      const { tradeType: encodedTradeType, notchesStrategy } = encodeTradeType(form.tradeType);
      await updateOptionsEngine(token, editingId, {
        name: form.name,
        symbol: form.symbol,
        tradeType: encodedTradeType,
        notchesStrategy,
        horizons: form.horizons,
        spreadWidth: form.spreadWidth,
        quantity: form.quantity,
        profitTargetPct: form.profitTargetPct,
        stopLossMultiplier: form.stopLossMultiplier,
        hardStopMultiplier: form.hardStopMultiplier,
        enableHardStopUnderlying: form.enableHardStopUnderlying,
        hardStopUnderlyingBuffer: form.hardStopUnderlyingBuffer,
        exitStrategy: form.exitStrategy,
        maxOpenPositions: form.maxOpenPositions,
        maxDailyTrades: form.maxDailyTrades,
        maxBpPct: form.maxBpPct,
        minSpreadCredit: form.minSpreadCredit === '' ? null : parseFloat(form.minSpreadCredit) / (MULTIPLIER[form.symbol] || 5),
        fillStrategy: form.fillStrategy,
        maxOrderAgeHours: form.fillStrategy === 'patient' ? form.maxOrderAgeHours : undefined,
        fillWalkSteps: form.fillWalkSteps,
        fillWalkIntervalMs: form.fillWalkIntervalMs,
        fillAdverseTicks: form.fillAdverseTicks,
        horizonStrategy: form.horizonStrategy,
        signalSource: form.signalSource,
        preset: form.preset,
        notchesMaxDte: form.notchesMaxDte,
        notchesMinDte: form.notchesMinDte,
        profitCloseMode: form.profitCloseMode,
        postProfitCooldownHours: form.postProfitCooldownHours,
        lossCloseMode: form.lossCloseMode,
        stopLossMinDte: form.stopLossMinDte,
        paperMode: form.paperMode,
        hedgeWithLongCall: form.hedgeWithLongCall,
        hedgeCallTargetDelta: form.hedgeCallTargetDelta,
        hedgeCallMinDTE: form.hedgeCallMinDTE,
        creditDegradationGate: {
          enabled: form.creditGateEnabled,
          maxPctBelowSignal: form.creditGateMaxPct,
          onBreach: form.creditGateOnBreach,
        },
      });
      resetForm();
      loadEngines();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const handleDelete = async (engineId: string) => {
    if (!token || !window.confirm('Delete this engine? This cannot be undone.')) return;
    try { await deleteOptionsEngine(token, engineId); loadEngines(); } catch (e: any) { setError(e.message); }
  };

  const handleToggleStatus = async (engine: OptionsEngine) => {
    if (!token) return;
    const newStatus = engine.status === 'active' ? 'paused' : 'active';
    try { await updateOptionsEngine(token, engine.engineId, { status: newStatus }); loadEngines(); } catch (e: any) { setError(e.message); }
  };

  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showRiskWarning, setShowRiskWarning] = useState<null | (() => void)>(null);

  // Reset disclaimer whenever any form field changes
  const setFormAndResetDisclaimer = (updater: (f: FormState) => FormState) => {
    setForm(updater);
    setDisclaimerAccepted(false);
  };

  const toggleHorizon = (h: number) => {
    setForm(f => ({ ...f, horizons: f.horizons.includes(h) ? f.horizons.filter(x => x !== h) : [...f.horizons, h] }));
  };

  const isLive = !form.paperMode;

  // Gate create/save behind disclaimer (live only) + naked/auto risk warning (live only)
  const handleSubmitWithChecks = () => {
    if (isLive && !disclaimerAccepted) { setError('You must accept the disclaimer before creating or saving a live engine.'); return; }
    const action = editingId ? handleSaveEdit : handleCreate;
    if (isLive && (form.tradeType === 'naked' || form.tradeType === 'auto')) {
      setShowRiskWarning(() => action);
    } else {
      action();
    }
  };

  const badge = (label: string, bg: string, fg: string) => (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color: fg, marginRight: 6 }}>{label}</span>
  );

  // Shared form fields for create and edit
  const renderForm = (isEdit: boolean) => (
    <div style={{ ...cardStyle, border: '1px solid #6d28d9' }}>
      <h3 style={{ color: '#f9fafb', margin: '0 0 16px 0' }}>{isEdit ? 'Edit Engine' : 'Create Options Engine'}</h3>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Engine Name</label>
        <input style={inputStyle} value={form.name} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, name: e.target.value }))} placeholder="e.g., ES 7-Day Spreads" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Symbol</label>
          <select style={selectStyle} value={form.symbol} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, symbol: e.target.value }))} disabled={isEdit}>
            {SYMBOLS.map(s => <option key={s.value} value={s.value}>{s.label} ({s.multiplier})</option>)}
          </select>
          {isEdit && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Symbol cannot be changed</div>}
        </div>
        <div>
          <label style={labelStyle}>Trade Type</label>
          <select style={selectStyle} value={form.tradeType} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, tradeType: e.target.value as TradeType }))}>
            <option value="spread">Credit Spread (defined risk)</option>
            <option value="naked">Naked Put (higher premium)</option>
            <option value="auto">Auto (smart selection)</option>
            <option value="bear_call_spread">Bear Call Spread (bearish, defined risk)</option>
            <option value="jade_lizard">Jade Lizard (bullish, 3 legs)</option>
            <option value="iron_condor">Iron Condor (neutral, 4 legs, defined risk)</option>
          </select>
          <div style={{ fontSize: 11, color: form.tradeType === 'naked' ? '#f59e0b' : form.tradeType === 'auto' ? '#3b82f6' : form.tradeType === 'bear_call_spread' ? '#a855f7' : form.tradeType === 'jade_lizard' ? '#f59e0b' : form.tradeType === 'iron_condor' ? '#22d3ee' : '#6b7280', marginTop: 2 }}>
            {form.tradeType === 'naked' ? 'Uncapped downside — margin required'
              : form.tradeType === 'auto' ? 'Chooses spread vs naked based on IV, delta, and liquidity each run'
              : form.tradeType === 'bear_call_spread' ? 'Bearish call credit spread. Strikes + width come from the BCS signal generator. Dark by default — kill switch must be flipped in service-config before any orders place.'
              : form.tradeType === 'jade_lizard' ? 'Bullish 3-leg (short put + short call + long call). Generator enforces no upside risk. Downside is uncapped (short put). Dark by default; generator produces 0 candidates today (pre-rewrite).'
              : form.tradeType === 'iron_condor' ? 'Neutral 4-leg (long put + short put + short call + long call). Defined risk on both wings. Dark by default; generator produces 0 candidates today (pre-rewrite).'
              : 'Max loss = spread width − credit'}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Mode</label>
          <select style={selectStyle} value={form.paperMode ? 'paper' : 'live'} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, paperMode: e.target.value === 'paper' }))}>
            <option value="paper">Paper (simulated fills)</option>
            <option value="live">Live (real money)</option>
          </select>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
            {form.paperMode
              ? 'Paper uses real market data + Greeks via your TastyTrade connection. Only the broker order placement is simulated.'
              : 'Live places real orders through your connected TastyTrade account.'}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Check Frequency</label>
          <select style={selectStyle} value={form.schedule} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, schedule: e.target.value }))} disabled={isEdit}>
            <option value="15min">Every 15 min</option>
            <option value="30min">Every 30 min</option>
            <option value="60min">Every 60 min</option>
          </select>
          {isEdit && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Delete and recreate to change</div>}
        </div>
      </div>

      {/* Signal source — picks where the trader gets candidates from */}
      <div style={{ marginBottom: 14, padding: 12, background: '#1e293b', borderRadius: 6, border: '1px solid #334155' }}>
        <label style={labelStyle}>Signal Source</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {(['notches', 'v2'] as SignalSource[]).map((src) => {
            const active = form.signalSource === src;
            return (
              <button
                key={src}
                type="button"
                onClick={() => setFormAndResetDisclaimer(f => ({ ...f, signalSource: src }))}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 6, cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  background: active ? '#6d28d9' : '#111827',
                  color: active ? '#f9fafb' : '#9ca3af',
                  border: active ? '1px solid #7c3aed' : '1px solid #374151',
                  textAlign: 'left',
                }}
              >
                <div>{src === 'notches' ? 'Notches (default)' : 'V2 (retiring)'}</div>
                <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.85, marginTop: 2 }}>
                  {src === 'notches'
                    ? 'Preset-driven, cross-DTE ranking. Pick preset + DTE cap below.'
                    : 'Legacy per-DTE iteration. Being retired — do not pick for new engines.'}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {form.signalSource === 'notches' ? (
        <>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Notches Preset</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['conservative', 'balanced', 'aggressive'] as NotchesPreset[]).map((p) => {
                const active = form.preset === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setFormAndResetDisclaimer(f => ({ ...f, preset: p }))}
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: 6, cursor: 'pointer',
                      fontSize: 13, fontWeight: 600,
                      background: active ? '#6d28d9' : '#111827',
                      color: active ? '#f9fafb' : '#9ca3af',
                      border: active ? '1px solid #7c3aed' : '1px solid #374151',
                      textAlign: 'left',
                    }}
                  >{PRESET_INFO[p].label}</button>
                );
              })}
            </div>
            <div style={{ marginTop: 8, padding: 8, background: '#0f172a', borderRadius: 4, fontSize: 12, color: '#94a3b8' }}>
              {PRESET_INFO[form.preset].short}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Notches min DTE</label>
              <input style={inputStyle} type="number" min={0} max={365} value={form.notchesMinDte}
                onChange={e => setFormAndResetDisclaimer(f => ({ ...f, notchesMinDte: parseInt(e.target.value) || 0 }))} />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                Skip candidates with fewer days to expiration than this (0 = no floor).
              </div>
            </div>
            <div>
              <label style={labelStyle}>Notches max DTE</label>
              <input style={inputStyle} type="number" min={1} max={365} value={form.notchesMaxDte}
                onChange={e => setFormAndResetDisclaimer(f => ({ ...f, notchesMaxDte: parseInt(e.target.value) || 60 }))} />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                Skip candidates beyond this DTE. Default 60.
              </div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Time Horizons (which DTEs to trade)</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ALL_HORIZONS.map(h => (
              <button key={h} onClick={() => toggleHorizon(h)} style={{
                padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: form.horizons.includes(h) ? '#6d28d9' : '#111827',
                color: form.horizons.includes(h) ? '#f9fafb' : '#6b7280',
                border: form.horizons.includes(h) ? '1px solid #7c3aed' : '1px solid #374151',
              }}>{h} DTE</button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        {form.tradeType !== 'naked' && form.tradeType !== 'bear_call_spread' && form.tradeType !== 'jade_lizard' && form.tradeType !== 'iron_condor' && <div /* hidden for naked-only + all multi-strategy branches (width comes from signal generator); shown for spread + auto */>
          <label style={labelStyle}>Spread Width (pts)</label>
          <input style={inputStyle} type="number" value={form.spreadWidth} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, spreadWidth: parseInt(e.target.value) || 50 }))} />
        </div>}
        <div>
          <label style={labelStyle}>Contracts</label>
          <input style={inputStyle} type="number" value={form.quantity} min={1} max={100} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} />
        </div>
        <div>
          <label style={labelStyle}>Max Open Positions</label>
          <input style={inputStyle} type="number" value={form.maxOpenPositions} min={1} max={20} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, maxOpenPositions: parseInt(e.target.value) || 3 }))} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Profit Target (%)</label>
          <input style={inputStyle} type="number" value={form.profitTargetPct} min={10} max={90} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, profitTargetPct: parseInt(e.target.value) || 50 }))} />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Close after capturing {form.profitTargetPct}% of credit (e.g. 75 = wait until 3/4 of premium captured)</div>
        </div>
        <div>
          <label style={labelStyle}>Stop Loss (multiplier)</label>
          <input style={inputStyle} type="number" step="0.5" value={form.stopLossMultiplier} min={1} max={5} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, stopLossMultiplier: parseFloat(e.target.value) || 2 }))} />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Close when spread reaches {form.stopLossMultiplier}x credit</div>
        </div>
        <div>
          <label style={labelStyle}>Hard Stop (multiplier)</label>
          <input
            style={inputStyle}
            type="number"
            step="0.5"
            value={form.hardStopMultiplier}
            min={0}
            max={10}
            onChange={e => setFormAndResetDisclaimer(f => ({ ...f, hardStopMultiplier: Math.max(0, parseFloat(e.target.value) || 0) }))}
          />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {form.hardStopMultiplier > 0
              ? `Unconditional close when spread reaches ${form.hardStopMultiplier}x credit — bypasses lossCloseMode=alerts. Only applies to NEW trades opened after saving.`
              : '0 = disabled. Set > 0 (e.g. 3) to enable a hard-stop tier that closes regardless of alerts mode.'}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Underlying Hard Stop</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' }}>
            <input
              type="checkbox"
              checked={form.enableHardStopUnderlying}
              onChange={e => setFormAndResetDisclaimer(f => ({ ...f, enableHardStopUnderlying: e.target.checked }))}
            />
            <span style={{ color: form.enableHardStopUnderlying ? '#4ade80' : '#9ca3af', fontSize: 13 }}>
              {form.enableHardStopUnderlying ? `Enabled (${Math.round(form.hardStopUnderlyingBuffer * 100)}% buffer)` : 'Disabled'}
            </span>
          </label>
          {form.enableHardStopUnderlying && (
            <input
              style={{ ...inputStyle, marginTop: 6 }}
              type="number"
              step="0.005"
              value={form.hardStopUnderlyingBuffer}
              min={0.005}
              max={0.20}
              onChange={e => {
                const v = parseFloat(e.target.value);
                setFormAndResetDisclaimer(f => ({
                  ...f,
                  hardStopUnderlyingBuffer: (Number.isFinite(v) && v > 0 && v < 0.5) ? v : 0.02,
                }));
              }}
            />
          )}
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {form.enableHardStopUnderlying
              ? `Force-close when spot crosses sellStrike ± ${Math.round(form.hardStopUnderlyingBuffer * 100)}% (put-side ⇒ below, call-side ⇒ above). Bypasses lossCloseMode=alerts. Only applies to NEW trades.`
              : 'When on, stamps a spot-price threshold on new trades. Ignores IV / bid-ask blowouts — cleanest defense against violent underlying moves.'}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Exit Strategy</label>
          <select
            style={inputStyle}
            value={form.exitStrategy}
            onChange={e => setFormAndResetDisclaimer(f => ({ ...f, exitStrategy: e.target.value as 'baseline' | 'convert' }))}
          >
            <option value="baseline">Baseline (2× stop)</option>
            <option value="convert">Convert (naked short → spread at 1.5×)</option>
          </select>
          <div style={{ fontSize: 11, color: form.exitStrategy === 'convert' ? '#a5b4fc' : '#6b7280', marginTop: 2 }}>
            {form.exitStrategy === 'convert'
              ? 'At 1.5× credit, buy a long leg to cap loss at spread width. Naked_put + naked_call only — other strategies fall through to baseline.'
              : 'Baseline 2× credit stop only. Naked shorts stay naked to expiry / stop.'}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Max Daily Trades</label>
          <input style={inputStyle} type="number" value={form.maxDailyTrades} min={1} max={10} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, maxDailyTrades: parseInt(e.target.value) || 2 }))} />
        </div>
        <div>
          <label style={labelStyle}>Max BP Usage (%)</label>
          <input style={inputStyle} type="number" value={form.maxBpPct} min={10} max={80} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, maxBpPct: parseInt(e.target.value) || 40 }))} />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Skip trades if BP usage exceeds {form.maxBpPct}%</div>
        </div>
        <div>
          <label style={labelStyle}>Min Credit ($)</label>
          <input
            style={inputStyle}
            type="number"
            step="5"
            min={5}
            value={form.minSpreadCredit}
            placeholder={`default $${DEFAULT_MIN_CREDIT_DOLLARS[form.symbol] ?? ''}`}
            onChange={e => setFormAndResetDisclaimer(f => ({ ...f, minSpreadCredit: e.target.value }))}
          />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {form.minSpreadCredit
              ? `Skip trades collecting less than $${form.minSpreadCredit} credit`
              : `Using default $${DEFAULT_MIN_CREDIT_DOLLARS[form.symbol] ?? '?'}. Leave blank for default.`}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Profit Close Mode</label>
          <select
            style={selectStyle}
            value={form.profitCloseMode}
            onChange={e => setFormAndResetDisclaimer(f => ({ ...f, profitCloseMode: e.target.value as ProfitCloseMode }))}
          >
            <option value="smart">Smart (default) — let deep-OTM near-expiry run</option>
            <option value="strict">Strict — always close at the target %</option>
          </select>
          <div style={{ fontSize: 11, color: form.profitCloseMode === 'smart' ? '#3b82f6' : '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
            {form.profitCloseMode === 'smart'
              ? `Skip the close if the short is very low delta (<0.10), the market moved favorably, the strike is deep OTM, and DTE ≤ 7 — let it expire worthless instead. Otherwise close after capturing ${form.profitTargetPct}% of credit.`
              : `Always close after capturing ${form.profitTargetPct}% of credit. No conditional hold.`}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Post-Profit Cooldown (hours)</label>
          <input
            style={inputStyle}
            type="number"
            min={0}
            max={24}
            value={form.postProfitCooldownHours}
            onChange={e => setFormAndResetDisclaimer(f => ({ ...f, postProfitCooldownHours: Math.max(0, Math.min(24, parseInt(e.target.value) || 0)) }))}
          />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
            {form.postProfitCooldownHours > 0
              ? `After a profit close, pause new opens for ${form.postProfitCooldownHours}h. IV is typically crushed right after a winning close — selling new premium then is selling at the local IV-low.`
              : 'No cooldown — new trades can open immediately after a profit close. Set 1-24h to wait for IV to normalize.'}
          </div>
        </div>
      </div>

      {/* Stop-loss safety group. Own bordered block because these two knobs
          together change real-money risk behavior. Profit closes are unaffected. */}
      <div style={{ marginBottom: 20, padding: '12px 14px', border: `1px solid ${form.lossCloseMode === 'alerts' ? '#fbbf24' : '#374151'}`, borderRadius: 8, background: form.lossCloseMode === 'alerts' ? '#78350f22' : '#111827' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Stop-Loss Action</label>
            <select
              style={selectStyle}
              value={form.lossCloseMode}
              onChange={e => setFormAndResetDisclaimer(f => ({ ...f, lossCloseMode: e.target.value as LossCloseMode }))}
            >
              <option value="auto">Auto-close on stop-loss (default)</option>
              <option value="alerts">Alerts only — never auto-close on loss</option>
            </select>
            <div style={{ fontSize: 11, color: form.lossCloseMode === 'alerts' ? '#fbbf24' : '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
              {form.lossCloseMode === 'alerts'
                ? 'Stop-loss triggers send an SMS and log an audit entry, but the position is HELD — you close manually if needed. Profit-target closes still fire automatically.'
                : `Auto-close on stop-loss triggers (${form.stopLossMultiplier}× entry credit or ITM confirmation). Standard behavior.`}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Stop-Loss Min DTE</label>
            <input
              style={inputStyle}
              type="number"
              min={0}
              max={120}
              value={form.stopLossMinDte}
              onChange={e => setFormAndResetDisclaimer(f => ({ ...f, stopLossMinDte: Math.max(0, Math.min(120, parseInt(e.target.value) || 0)) }))}
            />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
              {form.stopLossMinDte > 0
                ? `Above ${form.stopLossMinDte} DTE, stop-loss requires |Δ|≥0.35 (the existing "high probability of ITM" bar) to fire. Below, it triggers on the ${form.stopLossMultiplier}× price threshold as usual. Prevents closing 45-DTE trades on temporary IV spikes.`
                : 'Disabled — stop-loss fires purely on the price threshold at every DTE. Legacy behavior; the ES 6850P + INTC 110P closes on 2026-07-17 happened under this setting.'}
            </div>
          </div>
        </div>
      </div>

      {/* Credit-degradation gate (2026-10-07) -- rejects entries where live quote has
          decayed N ticks below the signal credit by the time we're about to send. */}
      <div style={{ marginBottom: 14, padding: 12, border: '1px solid #374151', borderRadius: 6, background: '#1f2937' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#e5e7eb', fontWeight: 600, marginBottom: 6, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.creditGateEnabled}
            onChange={e => setFormAndResetDisclaimer(f => ({ ...f, creditGateEnabled: e.target.checked }))}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          Credit Degradation Gate {form.creditGateEnabled ? <span style={{ color: '#10b981', fontSize: 11 }}>(ON)</span> : <span style={{ color: '#9ca3af', fontSize: 11 }}>(off)</span>}
        </label>
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8, lineHeight: 1.5 }}>
          Immediately before sending to TastyTrade, compare the live quote credit against the strategy's reference credit (the credit the engine would have expected for THIS execution shape — naked, spread, jade, iron condor). If the live credit has dropped more than the allowed percent below reference, the opportunity has decayed and the trade is skipped. Scales correctly across all strategies.
        </div>
        {form.creditGateEnabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Max % below reference credit</label>
              <input
                style={inputStyle}
                type="number"
                step="0.1"
                min={0.1}
                max={50}
                value={form.creditGateMaxPct}
                onChange={e => setFormAndResetDisclaimer(f => ({ ...f, creditGateMaxPct: Math.max(0.1, Math.min(50, parseFloat(e.target.value) || 1)) }))}
              />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Default 1%. Example: signal $1.14, max 1% → skip if live &lt; $1.1286.</div>
            </div>
            <div>
              <label style={labelStyle}>On breach</label>
              <select
                style={inputStyle}
                value={form.creditGateOnBreach}
                onChange={e => setFormAndResetDisclaimer(f => ({ ...f, creditGateOnBreach: (e.target.value === 'proceed' ? 'proceed' : 'skip') }))}
              >
                <option value="skip">Skip this cycle (recommended)</option>
                <option value="proceed">Proceed with live quote</option>
              </select>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {form.creditGateOnBreach === 'skip'
                  ? 'Audit-log the breach and wait for the next signal. Safest.'
                  : 'Audit-log the breach but still send the order at the live (degraded) credit. Never falls back to the stale signal credit.'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Long-call hedge -- paper-test from 2026-06-09 backtest (+33% on naked puts) */}
      <div style={{ marginBottom: 14, padding: 12, border: '1px solid #374151', borderRadius: 6, background: '#1f2937' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#e5e7eb', fontWeight: 600, marginBottom: 6, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.hedgeWithLongCall}
            onChange={e => setFormAndResetDisclaimer(f => ({ ...f, hedgeWithLongCall: e.target.checked }))}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          Long-Call Hedge {form.hedgeWithLongCall ? <span style={{ color: '#10b981', fontSize: 11 }}>(ON)</span> : <span style={{ color: '#9ca3af', fontSize: 11 }}>(off)</span>}
        </label>
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8, lineHeight: 1.5 }}>
          When ON, every naked-put trade also opens a long call at the target delta on the same expiration. Captures upside when the underlying rallies during the hold. Hedge call closes when the naked put closes (paired exit). Spread trades and short-DTE naked puts skipped automatically.
          <br />Validated 2026-06-09 backtest: +$3,942 / +33% over 49 ES trades, concentrated on long-DTE naked puts.
        </div>
        {form.hedgeWithLongCall && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Hedge call target delta</label>
              <input
                style={inputStyle}
                type="number"
                step="0.05"
                min={0.05}
                max={0.50}
                value={form.hedgeCallTargetDelta}
                onChange={e => setFormAndResetDisclaimer(f => ({ ...f, hedgeCallTargetDelta: Math.max(0.05, Math.min(0.50, parseFloat(e.target.value) || 0.20)) }))}
              />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>0.20 = conservative (cheap, far OTM). 0.30 = closer to ATM, more upside capture but more expensive.</div>
            </div>
            <div>
              <label style={labelStyle}>Minimum DTE to hedge</label>
              <input
                style={inputStyle}
                type="number"
                min={0}
                max={60}
                value={form.hedgeCallMinDTE}
                onChange={e => setFormAndResetDisclaimer(f => ({ ...f, hedgeCallMinDTE: Math.max(0, Math.min(60, parseInt(e.target.value) || 7)) }))}
              />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Skip hedge on naked puts with DTE below this. Default 7 — 0DTE/1DTE have no time for the call to participate.</div>
            </div>
          </div>
        )}
      </div>

      {form.signalSource !== 'notches' && (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Horizon Selection Strategy <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>(V2 only)</span></label>
          <select
            style={selectStyle}
            value={form.horizonStrategy}
            onChange={e => setFormAndResetDisclaimer(f => ({ ...f, horizonStrategy: e.target.value as HorizonStrategy }))}
          >
            <option value="lowest_dte">Fastest turnover (lowest DTE)</option>
            <option value="highest_score">Highest AI score</option>
            <option value="highest_credit">Biggest income per trade</option>
            <option value="best_theta">Best theta decay (credit/day)</option>
          </select>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, lineHeight: 1.5, padding: '8px 10px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 4 }}>
            <div style={{ color: '#cbd5e1', fontWeight: 600, marginBottom: 2 }}>{STRATEGY_INFO[form.horizonStrategy].label}</div>
            <div>{STRATEGY_INFO[form.horizonStrategy].short}</div>
            <div style={{ marginTop: 4, color: '#6b7280', fontStyle: 'italic' }}>Example: {STRATEGY_INFO[form.horizonStrategy].example}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Fill Strategy</label>
          <select style={selectStyle} value={form.fillStrategy} onChange={e => setFormAndResetDisclaimer(f => ({ ...f, fillStrategy: e.target.value as FillStrategy }))}>
            <option value="patient">Patient (GTC, wait for fill)</option>
            <option value="walk">Walk (active price improvement)</option>
          </select>
          <div style={{ fontSize: 11, color: form.fillStrategy === 'patient' ? '#3b82f6' : '#6b7280', marginTop: 2 }}>
            {form.fillStrategy === 'patient' ? 'Place GTC order, check fill on next engine run' : 'Place at mid, walk down each interval'}
          </div>
        </div>
        {form.fillStrategy === 'patient' ? (
          <div>
            <label style={labelStyle}>Max Order Age (hours)</label>
            <input style={inputStyle} type="number" min={1} max={72} value={form.maxOrderAgeHours}
              onChange={e => setFormAndResetDisclaimer(f => ({ ...f, maxOrderAgeHours: parseInt(e.target.value) || 24 }))} />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              Cancel unfilled orders older than {form.maxOrderAgeHours}h
            </div>
          </div>
        ) : (
          <>
            <div>
              <label style={labelStyle}>Walk Steps</label>
              <input style={inputStyle} type="number" min={1} max={5} value={form.fillWalkSteps}
                onChange={e => setFormAndResetDisclaimer(f => ({ ...f, fillWalkSteps: parseInt(e.target.value) || 3 }))} />
            </div>
            <div>
              <label style={labelStyle}>Walk Interval (sec)</label>
              <input style={inputStyle} type="number" min={5} max={60} value={Math.round(form.fillWalkIntervalMs / 1000)}
                onChange={e => setFormAndResetDisclaimer(f => ({ ...f, fillWalkIntervalMs: (parseInt(e.target.value) || 20) * 1000 }))} />
            </div>
          </>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>TastyTrade Account</label>
        {connections.length > 0 ? (
          <select style={selectStyle} value={selectedConnectionId} onChange={e => setSelectedConnectionId(e.target.value)} disabled={isEdit}>
            {connections.map(c => (
              <option key={c.connectionId} value={c.connectionId}>
                {c.label} (***{c.accountNumber?.slice(-4)})
              </option>
            ))}
          </select>
        ) : (
          <div style={{ fontSize: 13, color: '#f87171' }}>No TastyTrade accounts connected. Go to Profile to connect.</div>
        )}
        {isEdit && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Account cannot be changed after creation</div>}
      </div>

      {/* Disclaimer — live mode only */}
      {isLive && (
        <div style={{ marginBottom: 14, padding: '10px 14px', background: '#1e293b', borderRadius: 6, border: '1px solid #334155' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>
            <input type="checkbox" checked={disclaimerAccepted} onChange={e => setDisclaimerAccepted(e.target.checked)} style={{ marginTop: 4, flexShrink: 0 }} />
            <span>
              I understand that this auto-trading engine is in <strong style={{ color: '#f59e0b' }}>BETA</strong> and may execute real trades using my connected brokerage account.
              I accept full responsibility for any financial losses. Wealth Financial Systems provides tools and signals — not financial advice.
              Past performance does not guarantee future results. Options trading involves substantial risk of loss and is not suitable for all investors.
            </span>
          </label>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button style={btnPrimary} onClick={handleSubmitWithChecks} disabled={saving || !form.name.trim() || (form.signalSource === 'v2' && form.horizons.length === 0) || (isLive && !disclaimerAccepted)}>
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Engine'}
        </button>
        <button style={btnSecondary} onClick={resetForm}>Cancel</button>
      </div>
    </div>
  );

  if (!enabled) {
    return (
      <div className="dashboard-container"><Navigation />
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
          <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700 }}>Options Auto-Trader</h1>
          <div style={cardStyle}><p style={{ color: '#9ca3af', textAlign: 'center' }}>This feature is not enabled on your account. Contact support to get access.</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container"><Navigation />
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700, margin: 0 }}>
            Options Auto-Trader
            <span style={{ marginLeft: 10, padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700, background: '#f59e0b30', color: '#f59e0b', verticalAlign: 'middle', letterSpacing: '0.05em' }}>BETA</span>
          </h1>
          <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>Automated options trading from V2 signals via TastyTrade. <span style={{ color: '#f59e0b' }}>Beta — use at your own risk.</span></p>
        </div>
        {!showCreate && !editingId && (
          <button style={btnPrimary} onClick={() => { if (connections.length === 0) { window.location.href = '/profile'; } else { resetForm(); setShowCreate(true); } }}>
            {connections.length > 0 ? '+ New Engine' : 'Connect TastyTrade First'}
          </button>
        )}
      </div>

      {error && <div style={{ color: '#f87171', marginBottom: 12, padding: '8px 12px', background: '#7f1d1d22', borderRadius: 6 }}>{error}</div>}

      {/* Create / Edit Form */}
      {showCreate && renderForm(false)}
      {editingId && renderForm(true)}

      {/* Engine Cards */}
      {loading && <p style={{ color: '#6b7280' }}>Loading engines...</p>}

      {!loading && engines.length === 0 && !showCreate && (
        <div style={cardStyle}>
          <p style={{ color: '#9ca3af', textAlign: 'center' }}>No options trading engines yet. Click "+ New Engine" to get started.</p>
        </div>
      )}

      {engines.map(engine => {
        if (editingId === engine.engineId) return null; // Hide card when editing
        const cfg = engine.optionsConfig || {} as any;
        return (
          <div key={engine.engineId} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', color: '#f9fafb', fontSize: 18 }}>{engine.name}</h3>
                <div>
                  {badge(engine.paperMode ? 'PAPER' : 'LIVE', engine.paperMode ? '#78350f' : '#064e3b', engine.paperMode ? '#fbbf24' : '#4ade80')}
                  {badge(engine.status === 'active' ? 'ACTIVE' : 'PAUSED', engine.status === 'active' ? '#064e3b' : '#374151', engine.status === 'active' ? '#4ade80' : '#6b7280')}
                  {badge(cfg.symbol || 'ES', '#1e1b4b', '#a78bfa')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => handleEdit(engine)} style={btnSmall}>Edit</button>
                <button onClick={() => handleToggleStatus(engine)} style={btnSmall}>
                  {engine.status === 'active' ? 'Pause' : 'Resume'}
                </button>
                <button onClick={() => handleDelete(engine.engineId)} style={btnDanger}>Delete</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 16 }}>
              <div><div style={{ color: '#6b7280', fontSize: 11 }}>TastyTrade Account</div><div style={{ fontSize: 14, color: '#f9fafb', fontWeight: 600 }}>{engine.tastytradeAccountNumber ? '***' + engine.tastytradeAccountNumber.slice(-4) : '-'}</div></div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Signal Source</div>
                {(() => {
                  const explicit = cfg.signalSource;
                  // Runtime default is now 'notches' (V2 retiring). Show explicit
                  // V2 in amber so it stands out as needing migration.
                  const isV2 = explicit === 'v2';
                  const label = isV2
                    ? 'V2 (retiring)'
                    : `Notches / ${cfg.preset || 'conservative'}${explicit ? '' : ' (default)'}`;
                  const color = isV2 ? '#f59e0b' : '#8b5cf6';
                  return <div style={{ fontSize: 14, color }}>{label}</div>;
                })()}
              </div>
              {cfg.signalSource === 'v2' ? (
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Horizons</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{(cfg.horizons || []).map((h: number) => `${h}DTE`).join(', ')}</div></div>
              ) : (
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>DTE Range</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{cfg.notchesMinDte ?? 0}-{cfg.notchesMaxDte ?? 60}d</div></div>
              )}
              <div><div style={{ color: '#6b7280', fontSize: 11 }}>Type</div><div style={{ fontSize: 14, color: cfg.tradeType === 'naked' ? '#f59e0b' : cfg.tradeType === 'auto' ? '#3b82f6' : '#f9fafb' }}>{cfg.tradeType === 'naked' ? 'Naked' : cfg.tradeType === 'auto' ? 'Auto' : 'Spread'}{cfg.tradeType !== 'naked' ? ` ${cfg.spreadWidth || 50}pt` : ''}</div></div>
              <div><div style={{ color: '#6b7280', fontSize: 11 }}>Contracts</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{cfg.quantity || 1}</div></div>
              <div><div style={{ color: '#6b7280', fontSize: 11 }}>Profit Target</div><div style={{ fontSize: 14, color: '#4ade80' }}>{cfg.profitTargetPct || 50}%</div></div>
              <div><div style={{ color: '#6b7280', fontSize: 11 }}>Stop Loss</div><div style={{ fontSize: 14, color: '#f87171' }}>{cfg.stopLossMultiplier || 2}x</div></div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Hard Stop</div>
                <div style={{ fontSize: 14, color: (cfg.hardStopMultiplier || 0) > 0 ? '#4ade80' : '#6b7280', fontWeight: (cfg.hardStopMultiplier || 0) > 0 ? 600 : 400 }}>
                  {(cfg.hardStopMultiplier || 0) > 0 ? `${cfg.hardStopMultiplier}x` : 'off'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Underlying Stop</div>
                <div style={{ fontSize: 14, color: cfg.enableHardStopUnderlying ? '#4ade80' : '#6b7280', fontWeight: cfg.enableHardStopUnderlying ? 600 : 400 }}>
                  {cfg.enableHardStopUnderlying ? `${Math.round((cfg.hardStopUnderlyingBuffer ?? 0.02) * 100)}%` : 'off'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Convert Exit</div>
                <div style={{ fontSize: 14, color: cfg.exitStrategy === 'convert' ? '#a5b4fc' : '#6b7280', fontWeight: cfg.exitStrategy === 'convert' ? 600 : 400 }}>
                  {cfg.exitStrategy === 'convert' ? '1.5×' : 'off'}
                </div>
              </div>
              <div><div style={{ color: '#6b7280', fontSize: 11 }}>Max Positions</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{cfg.maxOpenPositions || 3}</div></div>
              <div><div style={{ color: '#6b7280', fontSize: 11 }}>Max Daily</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{cfg.maxDailyTrades || 2}</div></div>
              <div><div style={{ color: '#6b7280', fontSize: 11 }}>Max BP%</div><div style={{ fontSize: 14, color: '#fbbf24' }}>{cfg.maxBpPct || 40}%</div></div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Min Credit</div>
                <div style={{ fontSize: 14, color: cfg.minSpreadCredit != null ? '#a78bfa' : '#9ca3af' }}>
                  ${cfg.minSpreadCredit != null ? Math.round(cfg.minSpreadCredit * (MULTIPLIER[cfg.symbol] || 5)) : (DEFAULT_MIN_CREDIT_DOLLARS[cfg.symbol] ?? '?')}
                  {cfg.minSpreadCredit == null && <span style={{ fontSize: 10, marginLeft: 4 }}>(default)</span>}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Close Mode</div>
                <div style={{ fontSize: 14, color: (cfg.profitCloseMode || 'smart') === 'strict' ? '#fbbf24' : '#a5b4fc' }}>
                  {(cfg.profitCloseMode || 'smart') === 'strict' ? 'Strict' : 'Smart'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Post-Profit Cooldown</div>
                <div style={{ fontSize: 14, color: (cfg.postProfitCooldownHours ?? 0) > 0 ? '#a78bfa' : '#9ca3af' }}>
                  {(cfg.postProfitCooldownHours ?? 0) > 0 ? `${cfg.postProfitCooldownHours}h` : 'Off'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Stop-Loss Action</div>
                <div style={{ fontSize: 14, color: (cfg.lossCloseMode || 'auto') === 'alerts' ? '#fbbf24' : '#f87171' }}>
                  {(cfg.lossCloseMode || 'auto') === 'alerts' ? 'Alerts only' : 'Auto-close'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11 }}>Stop-Loss Min DTE</div>
                <div style={{ fontSize: 14, color: (cfg.stopLossMinDte ?? 21) > 0 ? '#a78bfa' : '#9ca3af' }}>
                  {(cfg.stopLossMinDte ?? 21) > 0 ? `${cfg.stopLossMinDte ?? 21}d` : 'Off'}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
    {/* Risk Warning Modal for naked/auto trades */}
    {showRiskWarning && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowRiskWarning(null)}>
        <div style={{ background: '#1e293b', border: '2px solid #dc2626', borderRadius: 12, padding: '28px 32px', maxWidth: 520, width: '90%', color: '#f1f5f9' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 28 }}>&#9888;&#65039;</span>
            <h2 style={{ margin: 0, fontSize: 20, color: '#dc2626' }}>Risk Warning — {form.tradeType === 'naked' ? 'Naked Put' : 'Auto Trade Type'}</h2>
          </div>
          {form.tradeType === 'naked' ? (
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <p style={{ margin: '0 0 10px' }}><strong>Naked put selling has uncapped downside risk.</strong> If the underlying drops significantly, your losses are theoretically unlimited and can exceed your account balance.</p>
              <p style={{ margin: '0 0 10px' }}>Unlike credit spreads, there is no protective long leg to cap your maximum loss. TastyTrade will require margin and may liquidate your position if the account falls below maintenance requirements.</p>
              <p style={{ margin: '0 0 10px', color: '#f59e0b' }}>This strategy is only suitable for experienced options traders who understand and accept the risk of significant capital loss.</p>
            </div>
          ) : (
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <p style={{ margin: '0 0 10px' }}><strong>Auto mode may select naked puts</strong> when the system determines the strike is far enough from the current price. Naked puts carry uncapped downside risk.</p>
              <p style={{ margin: '0 0 10px' }}>The system evaluates distance-to-strike and return-on-capital to choose between credit spreads and naked puts each run. You cannot predict which type will be selected in advance.</p>
              <p style={{ margin: '0 0 10px', color: '#f59e0b' }}>By enabling auto mode, you accept the possibility of naked put positions with theoretically unlimited loss potential.</p>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button style={{ ...btnPrimary, background: '#dc2626' }} onClick={() => { const action = showRiskWarning; setShowRiskWarning(null); action(); }}>
              I Understand the Risks — Proceed
            </button>
            <button style={btnSecondary} onClick={() => setShowRiskWarning(null)}>Cancel</button>
          </div>
        </div>
      </div>
    )}
      <RiskDisclaimer />
    </div>
  );
};

export default OptionsEngines;
